import axios, {
  AxiosResponse,
  InternalAxiosRequestConfig,
  RawAxiosRequestHeaders,
} from 'axios';
import { getUser } from './storage';

const api = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
});

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const headers: RawAxiosRequestHeaders = config.headers;
    const user = getUser();

    if (user) {
      headers.Authorization = `Bearer ${user.access}`;
    }

    // Always get fresh language from localStorage
    const currentLanguage = localStorage.getItem('i18nextLng') || 'en';
    const language = currentLanguage === 'ptbr' ? 'pt-br' : currentLanguage;
    headers['Accept-Language'] = language;

    return config;
  },
  (error) => {
    Promise.reject(error);
  },
);

export interface PaginatedListResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
  current_page_count: number;
}

type ItemResponse<TItem> = Promise<AxiosResponse<TItem>>;

export type AxiosPaginatedListResponse<TItem> = AxiosResponse<
  PaginatedListResponse<TItem>
>;

export type AxiosListResponse<TItem> = AxiosResponse<
  PaginatedListResponse<TItem> | TItem[]
>;

export interface DjangoGenericErrorData {
  detail: string;
}

export interface APIError {
  [key: string]: string[];
}

class BaseAPI {
  baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  buildUrl = (path: string | undefined = undefined): string => {
    if (!path) {
      return `${this.baseUrl}/`;
    }
    return `${this.baseUrl}/${path}/`;
  };

  list = async <TItem>(
    params: Record<string, unknown>,
  ): Promise<AxiosResponse<PaginatedListResponse<TItem>>> => {
    return api.get(this.buildUrl(), {
      params: params,
      paramsSerializer: {
        indexes: null,
      },
    });
  };

  getALl = async <TItem>(
    params: Record<string, unknown>,
  ): Promise<AxiosResponse<TItem[]>> => {
    return api.get(this.buildUrl(), {
      params: params,
    });
  };

  detail = async <TItem>(
    id: number | string | number[] | object,
    params?: Record<string, unknown>,
  ): ItemResponse<TItem> => {
    return api.get(this.buildUrl(id.toString()), { params });
  };

  update = async <TItem>(
    id: number | string,
    data: TItem | Record<string, unknown>,
  ): ItemResponse<TItem> => {
    return api.put(this.buildUrl(id.toString()), data);
  };

  create = async <TItem>(
    data: TItem | Record<string, unknown>,
  ): ItemResponse<TItem> => {
    return api.post(this.buildUrl(), data);
  };

  delete = async (id: number): Promise<AxiosResponse<undefined>> => {
    return api.delete(this.buildUrl(id.toString()));
  };

  select = async (params: Record<string, unknown>): Promise<AxiosResponse> => {
    return api.get(this.buildUrl('select'), { params });
  };
}

export { api, BaseAPI };
